import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip } from '@/shared/ui/states'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { GradeChip, label } from './components/primitives'
import { ingestItem, listSources, listSubjects, type Source, type Subject } from './api'
import { DatePicker } from '@/components/erp/date-picker'

const CREDIBILITY = [
  { value: '1', meaning: 'confirmed by other sources' },
  { value: '2', meaning: 'probably true' },
  { value: '3', meaning: 'possibly true' },
  { value: '4', meaning: 'doubtful' },
  { value: '5', meaning: 'improbable' },
  { value: '6', meaning: 'cannot be judged' },
]

const ITEM_TYPES = ['MANUAL_NOTE', 'FIELD_REPORT', 'COURT_RECORD', 'GAZETTE_NOTICE',
  'PUBLIC_STATEMENT', 'ALLEGATION', 'COMPANY_RECORD', 'ASSET_DECLARATION', 'CONTACT_LOG',
  'DOCUMENT']

const MIN_ATTESTATION = 20

/**
 * Manual entry — the attested door.
 *
 * ## The default credibility is 4, and the form does not hide that
 *
 * An analyst typing hearsay into a box should have to raise the grade deliberately, so the
 * selector opens on **4 — doubtful** and every option is spelled out in words. A default of
 * "probably true" would make the path of least resistance the one that quietly promotes a
 * rumour.
 *
 * ## The attestation is not decoration
 *
 * How you know this, at least {@link MIN_ATTESTATION} characters, recorded on the row and in
 * the audit trail against your name. The submit stays disabled at 19 characters — the server
 * refuses it anyway, and a form that let you press the button only to bounce would be worse
 * than one that says so up front.
 */
export function ManualEntryPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [sources, setSources] = useState<Source[]>([])
  const [subjectId, setSubjectId] = useState(params.get('subjectId') ?? '')
  const [sourceCode, setSourceCode] = useState('')
  const [itemTypeCode, setItemTypeCode] = useState('MANUAL_NOTE')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [url, setUrl] = useState('')
  const [occurredAt, setOccurredAt] = useState('')
  const [infoCredibility, setInfoCredibility] = useState('4')
  const [legalBasisCode, setLegalBasisCode] = useState('OPEN_SOURCE')
  const [attestationText, setAttestationText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const [s, src] = await Promise.all([listSubjects(), listSources()])
      setSubjects(s)
      // Only MANUAL and FIELD sources take a hand-typed item; the server refuses the rest,
      // so offering them here would be an invitation to a 422.
      //
      // `advisor-synthesis` is excluded even though it qualifies. That source exists so an
      // APPLIED MODEL PROPOSAL has provenance, and it is graded F — cannot be judged —
      // precisely because a language model asserted it. A human filing their own observation
      // under it would misattribute their work as a machine lead and bury it below the brief
      // floor, where nothing will read it again.
      const usable = src.filter((x) => (x.kindCode === 'MANUAL' || x.kindCode === 'FIELD')
        && x.code !== 'advisor-synthesis')
      setSources(usable)
      setSubjectId((current) => current || s[0]?.id || '')
      setSourceCode((current) => current || usable[0]?.code || '')
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const attestationShort = attestationText.trim().length < MIN_ATTESTATION
  const noDeskSource = sources.length === 0

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (attestationShort) return
    setBusy(true)
    setError(null)
    try {
      await ingestItem({
        subjectId, sourceCode, itemTypeCode, title,
        body: body || null,
        url: url || null,
        // Sent only when the analyst actually knows it. An unknown event date stays NULL
        // rather than defaulting to today — a guessed date sorts into the middle of the
        // timeline and reads as fact.
        occurredAt: occurredAt ? new Date(occurredAt).toISOString() : null,
        infoCredibility, legalBasisCode, attestationText,
      })
      navigate(`/dossier/${subjectId}`)
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-3">
      <SectionHeader
        title="Manual entry"
        description="One observation, attested. Custody is stamped by the server — there is no
                     field here for it, and there will not be."
      />

      {error && <ErrorStrip message={error} />}

      {noDeskSource && (
        <p className="rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1.5
                      text-[11px] text-amber-700 dark:text-amber-400">
          No MANUAL or FIELD source exists yet. Add one on Sources & grading first — every
          item is attributed to a source, and its reliability grade comes from there.
        </p>
      )}

      <Panel>
        <div className="grid gap-2 p-3 sm:grid-cols-2">
          <Field label="Subject">
            <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)} required
                    className="h-7 w-full rounded border border-input bg-background px-2
                               text-[12px]">
              <option value="">Choose a subject…</option>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>{s.displayName} ({label(s.subjectTypeCode)})</option>
              ))}
            </select>
          </Field>
          <Field label="Source">
            <select value={sourceCode} onChange={(e) => setSourceCode(e.target.value)} required
                    className="h-7 w-full rounded border border-input bg-background px-2
                               text-[12px]">
              <option value="">Choose a desk or field source…</option>
              {sources.map((s) => (
                <option key={s.id} value={s.code}>{s.name} ({s.reliability})</option>
              ))}
            </select>
          </Field>
          <Field label="Item type">
            <select value={itemTypeCode} onChange={(e) => setItemTypeCode(e.target.value)}
                    className="h-7 w-full rounded border border-input bg-background px-2
                               text-[12px]">
              {ITEM_TYPES.map((t) => <option key={t} value={t}>{label(t)}</option>)}
            </select>
          </Field>
          <Field label="Legal basis">
            <select value={legalBasisCode} onChange={(e) => setLegalBasisCode(e.target.value)}
                    className="h-7 w-full rounded border border-input bg-background px-2
                               text-[12px]">
              {['OPEN_SOURCE', 'PUBLIC_RECORD', 'LEGITIMATE_INTEREST', 'CONSENT']
                .map((b) => <option key={b} value={b}>{label(b)}</option>)}
            </select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Title">
              <input value={title} onChange={(e) => setTitle(e.target.value)} required
                     placeholder="What was observed, in one line"
                     className="h-7 w-full rounded border border-input bg-background px-2
                                text-[12px]" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Body">
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
                        className="w-full rounded border border-input bg-background px-2 py-1
                                   text-[12px]" />
            </Field>
          </div>
          <Field label="Source URL (optional)">
            <input value={url} onChange={(e) => setUrl(e.target.value)}
                   placeholder="https://…"
                   className="h-7 w-full rounded border border-input bg-background px-2
                              text-[12px]" />
          </Field>
          <Field label="When it happened (leave blank if unknown)">
            <DatePicker
              value={occurredAt}
              onChange={setOccurredAt}
              className="h-7 w-full rounded border border-input bg-background px-2 text-[12px]"
            />
          </Field>
        </div>
      </Panel>

      <Panel>
        <div className="space-y-2 p-3">
          <Field label="How credible is this claim?">
            <div className="flex items-center gap-2">
              <select value={infoCredibility}
                      onChange={(e) => setInfoCredibility(e.target.value)}
                      className="h-7 rounded border border-input bg-background px-2 text-[12px]">
                {CREDIBILITY.map((c) => (
                  <option key={c.value} value={c.value}>{c.value} — {c.meaning}</option>
                ))}
              </select>
              {/* The chip the reader will see, shown while you choose. */}
              <GradeChip grade={`${sources.find((s) => s.code === sourceCode)?.reliability
                ?? 'C'}${infoCredibility}`} />
              <span className="text-[10px] text-muted-foreground">
                the letter comes from the source, the number from you
              </span>
            </div>
          </Field>

          <Field label="Attestation — how you know this">
            <textarea value={attestationText} rows={2}
                      onChange={(e) => setAttestationText(e.target.value)}
                      placeholder="I was present at the county assembly sitting on Tuesday."
                      className="w-full rounded border border-input bg-background px-2 py-1
                                 text-[12px]" />
            <span className={`text-[10px] ${attestationShort ? 'text-amber-600'
              : 'text-muted-foreground'}`}>
              {attestationText.trim().length}/{MIN_ATTESTATION} characters minimum — recorded
              on the item and in the audit trail against your name
            </span>
          </Field>
        </div>
      </Panel>

      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy || attestationShort || noDeskSource}
                className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                           text-primary-foreground disabled:opacity-50">
          {busy ? 'Filing…' : 'File the observation'}
        </button>
        <button type="button" onClick={() => navigate('/dossier/subjects')}
                className="rounded border border-border px-3 py-1.5 text-[12px]">
          Cancel
        </button>
      </div>
    </form>
  )
}

function Field({ label: text, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{text}</span>
      {children}
    </label>
  )
}
